import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import api from '../utils/api';
import AiChatBox from '../components/AiChatBox';
import Loader from '../components/Loader';
import SearchableSelect from '../components/SearchableSelect';

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB per file — matches the server's multer limit
const EMPTY_PLAN_FORM = {
  course_code: '', course_name: '', course_level: '', program_type: '', description: '',
  require_guiding_language: false, require_course_preview: false,
};
const EMPTY_MODULE_FORM = { module_code: '', module_name: '', exam_weightage: '', pass_threshold: '50', language: 'English' };
const LANGUAGE_OPTIONS = ['English', 'Chinese'];

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// A course's own lifecycle, not an intake's — this is the "is the course
// real yet" pipeline. A course is a preview until it clears this workflow;
// only Approved/Locked courses appear in the Course Catalogue. Modules and
// intakes are decided afterwards, per-run, via Intake Master — unrelated
// to this stage.
const STAGES = [
  { s: 'Draft', d: 'Academic team drafts the new course — code, level, type, description. Not visible in the Catalogue yet.', c: 'var(--slate)', bg: 'var(--cloud)' },
  { s: 'Submitted', d: 'Proposal is complete and has been sent to the Academic Head for review.', c: 'var(--sky)', bg: 'var(--sky-dim)' },
  { s: 'Approved', d: 'Head has approved — the course is now live in the Course Catalogue. Intakes can be scheduled against it.', c: 'var(--green)', bg: 'var(--green-dim)' },
  { s: 'Locked', d: 'Course structure finalised — level, type, and description no longer editable from this workflow.', c: 'var(--gold)', bg: 'var(--gold-dim)' },
];

// Which external regulatory track a proposal needs to satisfy, inferred
// from its programme type. STB only licenses the Tourist Guide pathway;
// SkillsFuture-eligible courses run their WSQ/Skills Framework
// accreditation through SWDA (SSG and WSG merged into SWDA on 1 Jul 2026,
// replacing the old SSG portal); everything else is governed solely by
// TMIS's own EduTrust-certified academic process, not a per-course
// government submission.
function complianceTrack(programType) {
  const t = (programType || '').toLowerCase();
  if (t.includes('guid')) {
    return {
      label: 'STB Tourist Guide (ATO)', cls: 'p-sub',
      note: "STB Tourist Guide Training Programme track — needs the screening test, ≥120hr curriculum, and licence-eligible assessment on top of EduTrust.",
    };
  }
  if (t) {
    return {
      label: 'SWDA / WSQ', cls: 'p-ok',
      note: 'SkillsFuture-eligible course — align to the sector Skills Framework and SWDA’s WSQ accreditation on top of EduTrust.',
    };
  }
  return {
    label: 'EduTrust only', cls: 'p-draft',
    note: 'No external course-level accreditation — governed solely by TMIS’s own EduTrust-certified academic process.',
  };
}

function SectionLabel({ icon, children, first }) {
  return (
    <div className="ct" style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginTop: first ? 0 : '1.3rem', paddingTop: first ? 0 : '1.1rem', borderTop: first ? 'none' : '1px solid var(--border-soft)' }}>
      <i className={`fa-solid ${icon}`} style={{ color: 'var(--sea)', fontSize: '.78rem' }} /> {children}
    </div>
  );
}

const pstat = (s) => (s === 'Locked' ? 'p-lock' : s === 'Approved' ? 'p-ok' : s === 'Submitted' ? 'p-sub' : 'p-draft');
const pico = (s) => (s === 'Locked' ? 'fa-lock' : s === 'Approved' ? 'fa-circle-check' : s === 'Submitted' ? 'fa-paper-plane' : 'fa-pen-to-square');
const picol = (s) => (s === 'Locked' ? 'var(--gold)' : s === 'Approved' ? 'var(--green)' : s === 'Submitted' ? 'var(--sky)' : 'var(--slate)');
const picobg = (s) => (s === 'Locked' ? 'var(--gold-dim)' : s === 'Approved' ? 'var(--green-dim)' : s === 'Submitted' ? 'var(--sky-dim)' : 'var(--cloud)');

export default function WorkflowPage() {
  const { show } = useToast();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [newPlanOpen, setNewPlanOpen] = useState(false);
  const [planForm, setPlanForm] = useState(EMPTY_PLAN_FORM);
  const [planFiles, setPlanFiles] = useState([]);
  const [newModules, setNewModules] = useState([]); // modules being defined fresh for this proposal
  const [moduleForm, setModuleForm] = useState(EMPTY_MODULE_FORM);
  const [moduleFormError, setModuleFormError] = useState(null);
  const [planFormError, setPlanFormError] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    // ?all=1 — this page is the one place that needs to see Draft/Submitted
    // course proposals too, not just the published (Approved/Locked) ones
    // /catalogue shows by default.
    Promise.all([
      api.get('/course-master?all=1'),
      api.get('/course-levels').catch(() => ({ data: { data: [] } })),
    ])
      .then(([coursesRes, levelsRes]) => {
        setPlans(coursesRes.data.data || []);
        setLevels(levelsRes.data.data || []);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load deployment plans'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const levelName = (code) => levels.find((l) => l.level_code === code)?.level_name || null;

  const openNewPlan = () => {
    setPlanForm(EMPTY_PLAN_FORM);
    setPlanFiles([]);
    setNewModules([]);
    setModuleForm(EMPTY_MODULE_FORM);
    setModuleFormError(null);
    setPlanFormError(null);
    setNewPlanOpen(true);
  };
  const closeNewPlan = () => { setNewPlanOpen(false); };

  // A brand-new course doesn't have modules in the system yet — so instead
  // of picking from Module Master's existing list, the planner defines the
  // module here and it's created fresh when the plan is saved.
  const addNewModule = () => {
    const code = moduleForm.module_code.trim();
    const name = moduleForm.module_name.trim();
    const weightage = Number(moduleForm.exam_weightage);
    if (!code || !name) {
      setModuleFormError('Module code and name are required.');
      return;
    }
    if (newModules.some((m) => m.module_code.toLowerCase() === code.toLowerCase())) {
      setModuleFormError('That module code is already in this plan.');
      return;
    }
    if (!Number.isFinite(weightage) || weightage < 0 || weightage > 100) {
      setModuleFormError('Exam weightage must be a number between 0 and 100.');
      return;
    }
    setModuleFormError(null);
    setNewModules((prev) => [...prev, {
      module_code: code, module_name: name, exam_weightage: weightage,
      pass_threshold: Number(moduleForm.pass_threshold) || 50, language: moduleForm.language,
    }]);
    setModuleForm(EMPTY_MODULE_FORM);
  };
  const removeNewModule = (code) => setNewModules((prev) => prev.filter((m) => m.module_code !== code));

  const addPlanFiles = (fileList) => {
    const incoming = Array.from(fileList);
    const tooBig = incoming.filter((f) => f.size > MAX_FILE_BYTES);
    const ok = incoming.filter((f) => f.size <= MAX_FILE_BYTES);
    if (tooBig.length) {
      show(`${tooBig.map((f) => f.name).join(', ')} — over the 10MB limit, not added`, 'error');
    }
    if (ok.length) setPlanFiles((prev) => [...prev, ...ok]);
  };
  const removePlanFile = (idx) => setPlanFiles((prev) => prev.filter((_, i) => i !== idx));

  const submitNewPlan = async (e) => {
    e.preventDefault();
    if (!planForm.course_code.trim() || !planForm.course_name.trim()) {
      setPlanFormError('Course code and course name are required.');
      return;
    }
    setPlanFormError(null);
    setCreating(true);
    try {
      await api.post('/course-master', {
        course_code: planForm.course_code.trim(),
        course_name: planForm.course_name.trim(),
        course_level: planForm.course_level || null,
        program_type: planForm.program_type.trim() || null,
        description: planForm.description.trim() || null,
        require_guiding_language: planForm.require_guiding_language,
        require_course_preview: planForm.require_course_preview,
        deployment_status: 'Draft',
      });
      show(`${planForm.course_code.trim()} created as Draft`, 'success');
      const code = planForm.course_code.trim();
      if (newModules.length) {
        const createdCodes = [];
        for (const m of newModules) {
          try {
            await api.post('/modules', m);
            createdCodes.push(m.module_code);
          } catch (moduleErr) {
            show(moduleErr.response?.data?.message || `Failed to create module ${m.module_code}`, 'warning');
          }
        }
        if (createdCodes.length) {
          try {
            await api.put(`/course-master/${code}/modules`, { module_codes: createdCodes });
          } catch (linkErr) {
            show(linkErr.response?.data?.message || 'Modules created, but failed to link to the course', 'warning');
          }
        }
      }
      if (planFiles.length) {
        try {
          const formData = new FormData();
          planFiles.forEach((f) => formData.append('files', f));
          await api.post(`/course-master/${code}/attachments`, formData);
        } catch (uploadErr) {
          show(uploadErr.response?.data?.message || 'Course created, but attachments failed to upload', 'warning');
        }
      }
      closeNewPlan();
      load();
    } catch (err) {
      setPlanFormError(err.response?.data?.message || 'Failed to create course proposal');
    } finally {
      setCreating(false);
    }
  };

  const runAction = (courseCode, action, label) => {
    setBusyId(courseCode);
    api.post(`/course-master/${courseCode}/${action}`)
      .then((res) => {
        setPlans((prev) => prev.map((p) => (p.course_code === courseCode ? { ...p, deployment_status: res.data.deployment_status } : p)));
        show(`${label} — now ${res.data.deployment_status}`, action === 'reject' ? 'warning' : 'success');
      })
      .catch((err) => show(err.response?.data?.message || `Failed to ${action} plan`, 'error'))
      .finally(() => setBusyId(null));
  };

  const stageCounts = STAGES.reduce((acc, st) => {
    acc[st.s] = plans.filter((p) => p.deployment_status === st.s).length;
    return acc;
  }, {});

  return (
    <div>
      <div className="sh-ey">APPROVAL WORKFLOW · COURSE DEPLOYMENT</div>
      <div className="sh-ti">Deployment Plans</div>
      <div className="sh-de">Every new course is a preview until it clears Draft → Submitted → Approved → Locked. Only Approved/Locked courses appear in the Course Catalogue — modules and intakes are still decided afterwards, per run, via Intake Master.</div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="ct">Workflow Stages</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          {STAGES.map((s) => (
            <div key={s.s} style={{ background: s.bg, borderRadius: 9, padding: '1.05rem', border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '.68rem' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.7rem', fontWeight: 700, color: s.c, letterSpacing: '.1em' }}>{s.s.toUpperCase()}</div>
                <span className="pill" style={{ marginLeft: 'auto', background: 'var(--surface)', color: s.c, border: `1px solid ${s.c}` }}>{stageCounts[s.s] ?? 0} plan{stageCounts[s.s] === 1 ? '' : 's'}</span>
              </div>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-s)', lineHeight: 1.6 }}>{s.d}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="ai-agent-panel" style={{ marginBottom: '1.5rem' }}>
        <div className="ai-panel-header">
          <div className="ai-orb"><i className="fa-solid fa-diagram-next" style={{ color: '#93C5FD', fontSize: '1.1rem' }} /></div>
          <div><div className="ai-panel-title" style={{ fontSize: 'var(--text-lg)' }}>AI Workflow Agent</div><div className="ai-panel-sub">Ask the AI to validate plans, check compliance, or generate approval summaries</div></div>
        </div>
        <AiChatBox
          initialMessage="I can help validate new course proposals for EduTrust compliance, STB Tourist Guide requirements, and SkillsFuture (SWDA) eligibility. What would you like me to check?"
          placeholder="Ask about plan compliance, conflicts, or approvals…"
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '.75rem' }}>
        <div style={{ fontSize: 'var(--text-md)', fontWeight: 700, color: 'var(--ink)' }}>All Deployment Plans</div>
        <button className="btn btn-primary" onClick={openNewPlan}><i className="fa-solid fa-plus" /> New Plan</button>
      </div>

      {loading && <Loader label="Loading deployment plans…" />}
      {!loading && error && <div className="card" style={{ color: 'var(--red)' }}>{error}</div>}
      {!loading && !error && plans.length === 0 && (
        <div className="card" style={{ textAlign: 'center', color: 'var(--slate)', padding: '2.5rem 1rem' }}>
          No course proposals yet — click "New Plan" to draft one.
        </div>
      )}

      {!loading && !error && plans.map((p) => {
        const track = complianceTrack(p.program_type);
        return (
        <div key={p.course_code} className="plan-row">
          <div className="pl-ico" style={{ background: picobg(p.deployment_status) }}><i className={`fa-solid ${pico(p.deployment_status)}`} style={{ fontSize: '.95rem', color: picol(p.deployment_status) }} /></div>
          <div className="pl-in">
            <div className="pl-n">{p.course_name || p.course_code}</div>
            <div className="pl-m">
              <span><i className="fa-solid fa-hashtag" style={{ color: 'var(--slate-lt)' }} /> {p.course_code}</span>
              <span><i className="fa-solid fa-book" style={{ color: 'var(--slate-lt)' }} /> {p.module_count} module{p.module_count === 1 ? '' : 's'}</span>
              <span><i className="fa-solid fa-calendar-check" style={{ color: 'var(--slate-lt)' }} /> {p.intake_count} intake{p.intake_count === 1 ? '' : 's'}</span>
              <span><i className="fa-solid fa-user" style={{ color: 'var(--slate-lt)' }} /> {p.created_by_name || 'Unknown'}</span>
              {levelName(p.course_level) && <span className="pill" style={{ background: 'var(--chip-bg)', color: 'var(--slate)' }}>{levelName(p.course_level)}</span>}
              <span className={`pill ${track.cls}`} title={track.note}><i className="fa-solid fa-shield-halved" /> {track.label}</span>
            </div>
          </div>
          <div className="pl-ac">
            <span className={`pill ${pstat(p.deployment_status)}`}><span className="sdot" />{p.deployment_status}</span>
            {p.deployment_status === 'Submitted' && (
              <>
                <button className="btn btn-sea btn-sm" disabled={busyId === p.course_code} onClick={() => runAction(p.course_code, 'approve', `${p.course_code} approved`)}><i className="fa-solid fa-check" /> Approve</button>
                <button className="btn btn-danger btn-sm" disabled={busyId === p.course_code} onClick={() => runAction(p.course_code, 'reject', `${p.course_code} rejected`)}><i className="fa-solid fa-xmark" /> Reject</button>
              </>
            )}
            {p.deployment_status === 'Draft' && (
              <button className="btn btn-primary btn-sm" disabled={busyId === p.course_code} onClick={() => runAction(p.course_code, 'submit', `${p.course_code} submitted`)}><i className="fa-solid fa-paper-plane" /> Submit</button>
            )}
            {p.deployment_status === 'Approved' && (
              <button className="btn btn-gold btn-sm" disabled={busyId === p.course_code} onClick={() => runAction(p.course_code, 'lock', `${p.course_code} locked`)}><i className="fa-solid fa-lock" /> Lock</button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/catalogue')} title="View in Catalogue"><i className="fa-solid fa-eye" /></button>
          </div>
        </div>
        );
      })}

      {newPlanOpen && (
        <div className="sb-backdrop" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={closeNewPlan}>
          <form className="card modal" style={{ width: 620, maxWidth: '94vw' }} onClick={(e) => e.stopPropagation()} onSubmit={submitNewPlan}>
            <div className="modal-header">
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--sky-dim)', color: 'var(--sky)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className="fa-solid fa-diagram-next" style={{ fontSize: '.9rem' }} />
              </div>
              <div className="modal-title" style={{ flex: 1 }}>New Deployment Plan</div>
              <button type="button" className="modal-close" onClick={closeNewPlan} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
            </div>
            <div className="modal-body">
              {planFormError && <div className="lc-err" style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}><i className="fa-solid fa-triangle-exclamation" /> {planFormError}</div>}

              <SectionLabel icon="fa-graduation-cap" first>Course Details</SectionLabel>
              <div className="modal-row" style={{ display: 'flex', gap: '.8rem', flexWrap: 'wrap' }}>
                <div className="fg fg-tight" style={{ flex: '1 1 140px' }}>
                  <label className="fl">Course Code *</label>
                  <input className="finput" required value={planForm.course_code} onChange={(e) => setPlanForm((f) => ({ ...f, course_code: e.target.value }))} placeholder="e.g. TG401" />
                </div>
                <div className="fg fg-tight" style={{ flex: '2 1 240px' }}>
                  <label className="fl">Course Name *</label>
                  <input className="finput" required value={planForm.course_name} onChange={(e) => setPlanForm((f) => ({ ...f, course_name: e.target.value }))} placeholder="e.g. Advanced Nature Trail Guiding" />
                </div>
              </div>
              <div className="modal-row" style={{ display: 'flex', gap: '.8rem', flexWrap: 'wrap' }}>
                <div className="fg fg-tight" style={{ flex: '1 1 200px' }}>
                  <label className="fl"><i className="fa-solid fa-layer-group" style={{ color: 'var(--slate-lt)', marginRight: '.35rem' }} />Course Level</label>
                  <SearchableSelect
                    className="finput" value={planForm.course_level} onChange={(val) => setPlanForm((f) => ({ ...f, course_level: val }))}
                    placeholder="None"
                    options={[{ value: '', label: 'None' }, ...levels.map((l) => ({ value: l.level_code, label: l.level_name }))]}
                  />
                </div>
                <div className="fg fg-tight" style={{ flex: '1 1 200px' }}>
                  <label className="fl"><i className="fa-solid fa-tag" style={{ color: 'var(--slate-lt)', marginRight: '.35rem' }} />Programme Type</label>
                  <input className="finput" value={planForm.program_type} onChange={(e) => setPlanForm((f) => ({ ...f, program_type: e.target.value }))} placeholder="e.g. Tourist Guiding" />
                </div>
              </div>
              <div className="fg fg-tight">
                <label className="fl">Description</label>
                <textarea className="finput" rows={3} style={{ resize: 'vertical', minHeight: 70, width: '100%' }} value={planForm.description} onChange={(e) => setPlanForm((f) => ({ ...f, description: e.target.value }))} placeholder="What this course covers, who it's for…" />
              </div>

              <SectionLabel icon="fa-list-check">Requirements</SectionLabel>
              <div className="modal-row" style={{ display: 'flex', gap: '.7rem', flexWrap: 'wrap' }}>
                {[
                  { key: 'require_guiding_language', icon: 'fa-language', label: 'Requires guiding language' },
                  { key: 'require_course_preview', icon: 'fa-eye', label: 'Requires course preview' },
                ].map((req) => {
                  const on = planForm[req.key];
                  return (
                    <label
                      key={req.key}
                      style={{
                        flex: '1 1 200px', display: 'flex', alignItems: 'center', gap: '.6rem', cursor: 'pointer',
                        border: `1.5px solid ${on ? 'var(--sea-bg)' : 'var(--border-strong)'}`, borderRadius: 'var(--rs)',
                        padding: '.65rem .8rem', background: on ? 'var(--sea-dim)' : 'var(--surface)', transition: 'all .15s',
                      }}
                    >
                      <input type="checkbox" checked={on} onChange={(e) => setPlanForm((f) => ({ ...f, [req.key]: e.target.checked }))} style={{ accentColor: 'var(--sea)' }} />
                      <i className={`fa-solid ${req.icon}`} style={{ color: on ? 'var(--sea)' : 'var(--slate-lt)', fontSize: '.85rem' }} />
                      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--ink-s)', fontWeight: on ? 600 : 400 }}>{req.label}</span>
                    </label>
                  );
                })}
              </div>

              <SectionLabel icon="fa-book">Modules <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--slate-lt)' }}>— optional, define new ones rather than pick from existing</span></SectionLabel>
              {moduleFormError && <div className="lc-err" style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.6rem' }}><i className="fa-solid fa-triangle-exclamation" /> {moduleFormError}</div>}

              {!!newModules.length && (
                <div className="chip-row">
                  {newModules.map((m) => (
                    <span key={m.module_code} className="chip">
                      <i className="fa-solid fa-book-open" style={{ fontSize: '.7rem', opacity: .7 }} />
                      <span>{m.module_name} <span style={{ opacity: .7 }}>({m.exam_weightage}%)</span></span>
                      <button type="button" onClick={() => removeNewModule(m.module_code)} aria-label={`Remove ${m.module_name}`}><i className="fa-solid fa-xmark" /></button>
                    </span>
                  ))}
                </div>
              )}

              <div style={{ background: 'var(--cloud)', borderRadius: 'var(--rs)', padding: '.7rem', marginBottom: '.3rem' }}>
                <div className="modal-row" style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap', marginBottom: '.6rem' }}>
                  <div style={{ flex: '1 1 140px' }}>
                    <label className="fl" style={{ fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>Code</label>
                    <input className="finput" value={moduleForm.module_code} onChange={(e) => setModuleForm((f) => ({ ...f, module_code: e.target.value }))} placeholder="e.g. TG401-M1" />
                  </div>
                  <div style={{ flex: '2 1 240px' }}>
                    <label className="fl" style={{ fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>Module Name</label>
                    <input className="finput" value={moduleForm.module_name} onChange={(e) => setModuleForm((f) => ({ ...f, module_name: e.target.value }))} placeholder="e.g. Nature Reserve Interpretation" />
                  </div>
                </div>
                <div className="modal-row" style={{ display: 'flex', gap: '.6rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 110px' }}>
                    <label className="fl" style={{ fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>Weightage %</label>
                    <input className="finput" type="number" min="0" max="100" value={moduleForm.exam_weightage} onChange={(e) => setModuleForm((f) => ({ ...f, exam_weightage: e.target.value }))} placeholder="30" />
                  </div>
                  <div style={{ flex: '1 1 110px' }}>
                    <label className="fl" style={{ fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>Pass %</label>
                    <input className="finput" type="number" min="0" max="100" value={moduleForm.pass_threshold} onChange={(e) => setModuleForm((f) => ({ ...f, pass_threshold: e.target.value }))} />
                  </div>
                  <div style={{ flex: '1 1 110px' }}>
                    <label className="fl" style={{ fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>Language</label>
                    <select className="finput" value={moduleForm.language} onChange={(e) => setModuleForm((f) => ({ ...f, language: e.target.value }))}>
                      {LANGUAGE_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '.6rem' }}>
                  <button type="button" className="btn btn-outline btn-sm" onClick={addNewModule}><i className="fa-solid fa-plus" /> Add Module</button>
                </div>
              </div>

              <SectionLabel icon="fa-paperclip">Supporting Documents</SectionLabel>
              <div className="fg fg-tight">
                <label
                  htmlFor="planFileInput"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '.6rem', border: '1.5px dashed var(--border-strong)',
                    borderRadius: 'var(--rs)', padding: '.85rem', cursor: 'pointer', color: 'var(--slate)', fontSize: 'var(--text-sm)',
                  }}
                >
                  <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '1.1rem', color: 'var(--sea)' }} />
                  Click to add curriculum drafts, EduTrust/STB evidence, etc. — up to 10MB per file
                </label>
                <input
                  id="planFileInput" type="file" multiple style={{ display: 'none' }}
                  onChange={(e) => { addPlanFiles(e.target.files); e.target.value = ''; }}
                />
                {planFiles.length > 0 && (
                  <div style={{ marginTop: '.6rem', display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
                    {planFiles.map((f, i) => (
                      <div key={`${f.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', background: 'var(--cloud)', borderRadius: 'var(--rs)', padding: '.45rem .7rem' }}>
                        <i className="fa-solid fa-paperclip" style={{ color: 'var(--slate-lt)' }} />
                        <span style={{ flex: 1, fontSize: 'var(--text-sm)', color: 'var(--ink-s)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--slate-lt)', fontFamily: 'var(--font-mono)' }}>{formatBytes(f.size)}</span>
                        <button type="button" className="modal-close" style={{ width: 24, height: 24 }} onClick={() => removePlanFile(i)} aria-label={`Remove ${f.name}`}>
                          <i className="fa-solid fa-xmark" style={{ fontSize: '.75rem' }} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={closeNewPlan}><i className="fa-solid fa-xmark" /> Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={creating}>
                <i className={`fa-solid ${creating ? 'fa-spinner fa-spin' : 'fa-check'}`} />
                {creating ? 'Creating…' : 'Create Plan'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
